import React, { useState, useEffect, useRef, useCallback } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Grid,
  Typography,
  CircularProgress,
  Card,
  CardMedia,
  CardActionArea,
  Box,
  Tooltip,
  ButtonGroup,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@material-ui/core';
import ToggleButton from '@material-ui/lab/ToggleButton';
import ToggleButtonGroup from '@material-ui/lab/ToggleButtonGroup';
import ViewModuleIcon from '@material-ui/icons/ViewModule';
import ViewComfyIcon from '@material-ui/icons/ViewComfy';
import Lightbox from 'react-image-lightbox';
import 'react-image-lightbox/style.css'; // Ensure lightbox styles are imported
import { FixedSizeGrid as VirtualGrid, FixedSizeList as VirtualList, areEqual } from 'react-window';

import ShowcaseCard from './order/SessionCard';
import gql from 'graphql-tag';
import { Subscription, Query } from 'react-apollo';
import BoothIdLink from '../reusable/BoothIdLink';
const moment = require('moment');

/**
 * @typedef {object} Photo
 * @property {string} id - The ID of the photo.
 * @property {string} name - The name of the photo file.
 * @property {string} full_url - The full URL to the photo.
 * @property {string} [thumb_url] - The URL to the photo thumbnail (optional).
 * @property {number} [width] - The width of the photo (optional).
 * @property {number} [height] - The height of the photo (optional).
 * @property {string} createdat - Creation timestamp.
 * @property {string} [title] - Title (optional).
 * @property {string} [description] - Description (optional).
 * @property {string} [format] - e.g. "image/jpeg"
 */

/**
 * @typedef {object} RawSessionFromSubscription
 * @property {string} id - The ID of the session.
 * @property {string} createdat - The creation timestamp of the session.
 * @property {Array<Photo>} photos - Original list of photos from GraphQL.
 * @property {string} [booth_id] - Booth ID (optional).
 * @property {string} [order_id] - Order ID (optional).
 * @property {string} [title] - Session title (optional).
 * @property {object} [metadata] - Any other metadata (optional).
 */

/**
 * @typedef {RawSessionFromSubscription & {
 *  derivedMessage: Photo | undefined,
 *  derivedStrip: Photo | undefined,
 *  derivedPhotos: Array<Photo>,
 *  isNew: boolean
 * }} DisplayedSession
 */

// Date range options
const DATE_RANGES = {
  '72_HOURS': {
    label: 'Last 72 hours',
    getRange: () => {
      return {
        start: moment().subtract(72, 'hours').format(),
        end: moment().add(72, 'hours').format(), // Add 72 hours in the future
        label: 'Last 72 hours',
      };
    },
  },
  TODAY: {
    label: 'Today',
    getRange: () => {
      return {
        start: moment().startOf('day').format(),
        end: moment().endOf('day').add(72, 'hours').format(), // Add 72 hours for new photos
        label: 'Today',
      };
    },
  },
  YESTERDAY: {
    label: 'Yesterday',
    getRange: () => {
      return {
        start: moment().subtract(1, 'days').startOf('day').format(),
        end: moment().subtract(1, 'days').endOf('day').format(),
        label: 'Yesterday',
      };
    },
  },
  LAST_7_DAYS: {
    label: 'Last 7 days',
    getRange: () => {
      return {
        start: moment().subtract(7, 'days').format(),
        end: moment().add(72, 'hours').format(), // Add 72 hours for new photos
        label: 'Last 7 days',
      };
    },
  },
  LAST_30_DAYS: {
    label: 'Last 30 days',
    getRange: () => {
      return {
        start: moment().subtract(30, 'days').format(),
        end: moment().add(72, 'hours').format(), // Add 72 hours for new photos
        label: 'Last 30 days',
      };
    },
  },
  THIS_MONTH: {
    label: 'This month',
    getRange: () => {
      return {
        start: moment().startOf('month').format(),
        end: moment().endOf('month').format(),
        label: 'This month',
      };
    },
  },
  LAST_MONTH: {
    label: 'Last month',
    getRange: () => {
      return {
        start: moment().subtract(1, 'month').startOf('month').format(),
        end: moment().subtract(1, 'month').endOf('month').format(),
        label: 'Last month',
      };
    },
  },
  ALL_TIME: {
    label: 'All time',
    getRange: () => {
      return {
        start: null,
        end: null,
        label: 'All time',
      };
    },
  },
};

// Minimal subscription for initial load - only essential fields
const SESSIONS_SUBSCRIPTION_MINIMAL = gql`
  subscription OnNewSessions($startDate: timestamptz, $endDate: timestamptz) {
    sessions(
      order_by: { createdat: desc }
      where: { _and: [{ createdat: { _gte: $startDate } }, { createdat: { _lte: $endDate } }] }
    ) {
      id
      order_id
      createdat
      layout_id
      order {
        booth_id
        event_name
      }
      photos(order_by: { sequence: asc }) {
        id
        name
        thumb_url
        createdat
        sequence
      }
    }
  }
`;

// Query to fetch full_urls for specific photo IDs when needed (e.g., when opening lightbox)
const GET_PHOTOS_FULL_URLS = gql`
  query GetPhotosFullUrls($photoIds: [Int!]!) {
    photos(where: { id: { _in: $photoIds } }) {
      id
      full_url
      thumb_url
    }
  }
`;

const useStyles = makeStyles(theme => ({
  '@global': {
    '@keyframes fadeIn': {
      '0%': {
        opacity: 0,
        transform: 'translateY(20px)',
      },
      '100%': {
        opacity: 1,
        transform: 'translateY(0)',
      },
    },
    '.fadeIn': {
      animation: 'fadeIn 0.8s ease-out forwards',
    },
    '.session-card-enter': {
      opacity: 0,
      transform: 'translateY(20px)',
    },
    '.session-card-enter-active': {
      opacity: 1,
      transform: 'translateY(0)',
      transition: 'opacity 300ms ease-out, transform 300ms ease-out',
    },
    // Ensure lightbox z-index is high enough
    '.ReactModalPortal > div': {
      zIndex: `${theme.zIndex.modal + 100} !important`,
    },
    '.ReactModalPortal .ReactModal__Overlay': {
      zIndex: `${theme.zIndex.modal + 100} !important`,
    },
    '.ReactModalPortal .ReactModal__Content': {
      zIndex: `${theme.zIndex.modal + 101} !important`,
    },
  },
  root: {
    padding: theme.spacing(3),
    flexGrow: 1,
  },
  gridContainer: {
    marginTop: theme.spacing(2),
  },
  paper: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
  },
  centered: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '50vh',
    flexDirection: 'column',
  },
  toggleContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  toggleButton: {
    padding: theme.spacing(0.5, 1.5),
    '&.Mui-selected': {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      '&:hover': {
        backgroundColor: theme.palette.primary.dark,
      },
    },
  },
  filterButton: {
    fontSize: '0.75rem',
    padding: theme.spacing(0.5, 1),
    minWidth: 'auto',
    '&.Mui-selected': {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      '&:hover': {
        backgroundColor: theme.palette.primary.dark,
      },
    },
  },
  filtersContainer: {
    maxWidth: '70%',
    overflowX: 'auto',
    '&::-webkit-scrollbar': {
      height: '4px',
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: theme.palette.background.default,
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: theme.palette.primary.light,
      borderRadius: '4px',
    },
  },
  toggleIcon: {
    fontSize: '1rem',
  },
  toggleLabel: {
    fontSize: '0.75rem',
    marginLeft: theme.spacing(0.5),
  },
  photoItem: {
    width: '100%',
    paddingTop: '100%', // 1:1 Aspect Ratio for photo container
    position: 'relative',
  },
  photoMedia: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundSize: 'cover',
  },
  photoInfo: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    width: '100%',
    padding: theme.spacing(1),
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    color: '#fff',
    transition: 'transform 0.3s ease-in-out',
    transform: 'translateY(0)',
  },
  photoInfoText: {
    fontSize: '0.7rem',
    lineHeight: 1.2,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    marginBottom: '2px',
  },
  photoInfoLabel: {
    fontWeight: 'bold',
    marginRight: '4px',
  },
  captureTime: {
    position: 'absolute',
    top: 0,
    right: 0,
    padding: theme.spacing(0.5, 0.75),
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    color: '#fff',
    fontSize: '0.65rem',
    borderBottomLeftRadius: '4px',
  },
  eventName: {
    position: 'absolute',
    top: 0,
    left: 0,
    padding: theme.spacing(0.5, 0.75),
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    color: '#fff',
    fontSize: '0.65rem',
    borderBottomRightRadius: '4px',
    maxWidth: '70%',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  boothIdWrapper: {
    position: 'absolute',
    bottom: 0,
    right: 0,
    zIndex: 10,
    padding: theme.spacing(0.5),
  },
  newItem: {
    animation: '$fadeIn 0.8s ease-out forwards',
  },
  filterRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 0, // Remove margin to prevent visible line
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    position: 'sticky',
    top: 0,
    zIndex: theme.zIndex.appBar,
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1), // Extra padding for smooth fade effect
    paddingLeft: theme.spacing(3), // Match root padding
    paddingRight: theme.spacing(3), // Match root padding
    // Full width background with very smooth gradual fade from page color to transparent
    // Fade starts earlier and extends through the entire padding area
    //background: `linear-gradient(to bottom, ${theme.palette.background.paper} 0%, ${theme.palette.background.paper} 25%, rgba(255, 255, 255, 0.95) 40%, rgba(255, 255, 255, 0.85) 55%, rgba(255, 255, 255, 0.65) 70%, rgba(255, 255, 255, 0.4) 80%, rgba(255, 255, 255, 0.15) 90%, transparent 100%)`,
    // Remove any borders/shadows
    border: 'none',
    boxShadow: 'none',
    // Ensure full width
    width: 'calc(100% + 48px)', // Offset root padding (2 * theme.spacing(3) = 24px * 2 = 48px)
    marginLeft: -theme.spacing(3), // Offset root padding
    marginRight: -theme.spacing(3), // Offset root padding
  },
  dateRangeFilter: {
    minWidth: 140,
    maxWidth: 160,
    // Remove borders and background from input
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'transparent',
      '& fieldset': {
        border: 'none', // Remove border
      },
      '&:hover fieldset': {
        border: 'none', // Remove border on hover
      },
      '&.Mui-focused fieldset': {
        border: 'none', // Remove border when focused
      },
    },
    '& .MuiInputLabel-outlined': {
      transform: 'translate(14px, 12px) scale(0.9)',
    },
    '& .MuiInputLabel-shrink': {
      transform: 'translate(14px, -6px) scale(0.75)',
    },
    '& .MuiSelect-root': {
      paddingTop: 9,
      paddingBottom: 9,
      fontSize: '0.8rem',
      backgroundColor: 'transparent',
    },
    '& .MuiOutlinedInput-input': {
      paddingRight: 28,
      backgroundColor: 'transparent',
    },
  },
  viewToggleContainer: {
    flex: '0 0 auto',
  },
  virtualGridContainer: {
    width: '100%',
    // Use viewport height for virtualization performance
    // Hide embedded scrollbars - style to allow page scrolling
    '& > div[style*="overflow"]': {
      // Target react-window's outer scrollable container
      scrollbarWidth: 'none', // Firefox - hide scrollbar
      '&::-webkit-scrollbar': {
        display: 'none', // Chrome, Safari - hide scrollbar
        width: 0,
        height: 0,
      },
      // Allow scrolling but hide the scrollbar
      overflowY: 'auto',
      overflowX: 'hidden',
    },
  },
  virtualListContainer: {
    width: '100%',
    // Use viewport height for virtualization performance
    // Hide embedded scrollbars - style to allow page scrolling
    '& > div[style*="overflow"]': {
      // Target react-window's outer scrollable container
      scrollbarWidth: 'none', // Firefox - hide scrollbar
      '&::-webkit-scrollbar': {
        display: 'none', // Chrome, Safari - hide scrollbar
        width: 0,
        height: 0,
      },
      // Allow scrolling but hide the scrollbar
      overflowY: 'auto',
      overflowX: 'hidden',
    },
  },
}));

// Hook to get responsive column count
const useColumnCount = () => {
  const [columnCount, setColumnCount] = useState(() => {
    if (typeof window === 'undefined') return 6;
    const width = window.innerWidth;
    if (width < 600) return 2; // xs
    if (width < 960) return 3; // sm
    if (width < 1280) return 4; // md
    if (width < 1920) return 6; // lg
    return 8; // xl
  });

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      let cols = 6;
      if (width < 600) cols = 2;
      else if (width < 960) cols = 3;
      else if (width < 1280) cols = 4;
      else if (width < 1920) cols = 6;
      else cols = 8;
      setColumnCount(cols);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return columnCount;
};

// Virtualized photo grid cell component
// @ts-ignore - react-window provides these props
const PhotoCell = React.memo(({ columnIndex, rowIndex, style, data }) => {
  const { photos, columnCount, onPhotoClick, itemSize } = data;
  const index = rowIndex * columnCount + columnIndex;

  if (index >= photos.length) return null;

  const photo = photos[index];

  return (
    <div style={style}>
      <div style={{ padding: 8 }}>
        <Card raised={false} style={{ width: itemSize, height: itemSize }}>
          <CardActionArea onClick={() => onPhotoClick(photos, index)}>
            <Box position='relative' style={{ width: '100%', paddingTop: '100%' }}>
              <CardMedia
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  backgroundSize: 'cover',
                }}
                image={photo.thumb_url || photo.full_url || ''}
                title={`Photo ${index + 1}`}
              />
              {photo.eventName && (
                <Tooltip title={`Event: ${photo.eventName}`}>
                  <Box
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      padding: '4px 6px',
                      backgroundColor: 'rgba(0, 0, 0, 0.6)',
                      color: '#fff',
                      fontSize: '0.65rem',
                      borderBottomRightRadius: '4px',
                      maxWidth: '70%',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {photo.eventName}
                  </Box>
                </Tooltip>
              )}
              <Tooltip title={`Captured: ${moment(photo.createdat).format('MMMM Do YYYY, h:mm:ss a')}`}>
                <Box
                  style={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    padding: '4px 6px',
                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                    color: '#fff',
                    fontSize: '0.65rem',
                    borderBottomLeftRadius: '4px',
                  }}
                >
                  {moment(photo.createdat).fromNow()}
                </Box>
              </Tooltip>
              <Box
                style={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  width: '100%',
                  padding: '8px',
                  backgroundColor: 'rgba(0, 0, 0, 0.6)',
                  color: '#fff',
                }}
              >
                <Typography component='div' style={{ fontSize: '0.7rem', lineHeight: 1.2 }}>
                  <span style={{ fontWeight: 'bold', marginRight: '4px' }}>Order:</span>
                  {photo.orderId || 'N/A'}
                </Typography>
              </Box>
              {photo.boothId && (
                <Box
                  style={{
                    position: 'absolute',
                    bottom: 0,
                    right: 0,
                    zIndex: 10,
                    padding: '4px',
                  }}
                >
                  <BoothIdLink boothId={photo.boothId} size='small' />
                </Box>
              )}
            </Box>
          </CardActionArea>
        </Card>
      </div>
    </div>
  );
}, areEqual);

// Virtualized session list row component
// Renders 6 sessions per row on desktop
// @ts-ignore - react-window provides these props
const SessionRow = React.memo(({ index, style, data }) => {
  const { sessions, classes, containerWidth, sessionsPerRow } = data;
  const baseIndex = index * sessionsPerRow;
  const gap = 16; // Gap between sessions
  const padding = 24; // Side padding
  const itemWidth = Math.floor((containerWidth - padding * 2 - gap * (sessionsPerRow - 1)) / sessionsPerRow);

  const sessionItems = [];
  for (let i = 0; i < sessionsPerRow; i++) {
    const session = sessions[baseIndex + i];
    if (session) {
      sessionItems.push(
        <div key={session.id || baseIndex + i} style={{ width: itemWidth, flex: '0 0 auto' }}>
          <div className={session.isNew ? classes.newItem : ''}>
            <ShowcaseCard
              photos={session.derivedPhotos}
              message={session.derivedMessage}
              strip={session.derivedStrip}
              title={moment(session.createdat).fromNow()}
              caption={session.id}
            />
          </div>
        </div>
      );
    }
  }

  return (
    <div style={style}>
      <div style={{ display: 'flex', gap: `${gap}px`, padding: `0 ${padding}px` }}>{sessionItems}</div>
    </div>
  );
}, areEqual);

function RecentSessionsPage() {
  const classes = useStyles();
  const [viewMode, setViewMode] = useState('sessions'); // 'sessions' or 'photos'
  const [selectedOrderId, setSelectedOrderId] = useState('all'); // Default to 'all'
  const [selectedDateRange, setSelectedDateRange] = useState('LAST_30_DAYS'); // Default to last 30 days
  const [dateRange, setDateRange] = useState(DATE_RANGES['LAST_30_DAYS'].getRange());
  const prevSessionIds = useRef(new Set());
  const columnCount = useColumnCount();

  // Lightbox state for individual photos view
  const [lightboxIsOpen, setLightboxIsOpen] = useState(false);
  const [lightboxPhotoIndex, setLightboxPhotoIndex] = useState(0);
  const [lightboxImages, setLightboxImages] = useState(/** @type {Array<string>} */ ([]));
  const [lightboxPhotos, setLightboxPhotos] = useState(/** @type {Array<any>} */ ([])); // Store photo objects for querying
  const [needsFullUrls, setNeedsFullUrls] = useState(false);

  const containerRef = useRef(null);

  // Update date range when selection changes
  useEffect(() => {
    setDateRange(DATE_RANGES[selectedDateRange].getRange());
  }, [selectedDateRange]);

  const handleViewModeChange = (event, newViewMode) => {
    if (newViewMode !== null) {
      setViewMode(newViewMode);
    }
  };

  const handleOrderFilterChange = orderId => {
    setSelectedOrderId(orderId);
  };

  const handleDateRangeChange = event => {
    setSelectedDateRange(event.target.value);
  };

  const openPhotoLightbox = useCallback((photos, index) => {
    // Check if any photos are missing full_url
    const missingFullUrls = photos.some(p => !p.full_url && p.id);

    // Store photo objects for querying if needed
    setLightboxPhotos(photos);
    setNeedsFullUrls(missingFullUrls);

    // Use thumb_urls initially, will be replaced with full_urls once loaded
    const imageUrls = photos.map(p => p.full_url || p.thumb_url || '').filter(Boolean);

    setLightboxImages(imageUrls);
    setLightboxPhotoIndex(index);
    setLightboxIsOpen(true);
  }, []);

  // Callback to update lightbox images with full URLs from query
  const updateLightboxWithFullUrls = useCallback(
    photoData => {
      if (!photoData || !photoData.photos) return;

      // Create a map of photo IDs to full_urls
      const fullUrlMap = new Map();
      photoData.photos.forEach(photo => {
        if (photo.id && photo.full_url) {
          fullUrlMap.set(photo.id, photo.full_url);
        }
      });

      // Update images array with full URLs where available
      const updatedImages = lightboxPhotos
        .map(photo => {
          if (photo.id && fullUrlMap.has(photo.id)) {
            return fullUrlMap.get(photo.id);
          }
          // Keep existing full_url or thumb_url
          return photo.full_url || photo.thumb_url || '';
        })
        .filter(Boolean);

      setLightboxImages(updatedImages);
      setNeedsFullUrls(false); // Mark as loaded
    },
    [lightboxPhotos]
  );

  // Extract photo IDs that need full URLs
  const photoIdsForQuery =
    needsFullUrls && lightboxPhotos.length > 0 ? lightboxPhotos.filter(p => p.id && !p.full_url).map(p => p.id) : [];

  return (
    <div className={classes.root}>
      {/* Query for full URLs when lightbox opens and photos are missing full_url */}
      {lightboxIsOpen && needsFullUrls && photoIdsForQuery.length > 0 && (
        <Query
          query={GET_PHOTOS_FULL_URLS}
          variables={{ photoIds: photoIdsForQuery }}
          fetchPolicy='network-only'
          onCompleted={updateLightboxWithFullUrls}
        >
          {() => null}
        </Query>
      )}

      {lightboxIsOpen && lightboxImages.length > 0 && (
        <Lightbox
          mainSrc={lightboxImages[lightboxPhotoIndex]}
          nextSrc={lightboxImages[(lightboxPhotoIndex + 1) % lightboxImages.length]}
          prevSrc={lightboxImages[(lightboxPhotoIndex + lightboxImages.length - 1) % lightboxImages.length]}
          onCloseRequest={() => {
            setLightboxIsOpen(false);
            setNeedsFullUrls(false);
            setLightboxPhotos([]);
          }}
          onMovePrevRequest={() =>
            setLightboxPhotoIndex((lightboxPhotoIndex + lightboxImages.length - 1) % lightboxImages.length)
          }
          onMoveNextRequest={() => setLightboxPhotoIndex((lightboxPhotoIndex + 1) % lightboxImages.length)}
          reactModalStyle={{
            overlay: { zIndex: 1000000 }, // Ensure high zIndex
            content: { zIndex: 1000000 },
          }}
        />
      )}
      <div>
        <div className={classes.filterRow}>
          {/* Order Filter Buttons */}
          <div className={classes.filtersContainer}>
            <Subscription
              subscription={SESSIONS_SUBSCRIPTION_MINIMAL}
              variables={{
                startDate: dateRange.start,
                endDate: dateRange.end,
              }}
            >
              {result => {
                const { data, loading, error } = result || {};
                // If no data yet, render just the "All" button
                if (loading || error || !data || !data.sessions) {
                  return (
                    <ButtonGroup size='small' color='primary'>
                      <Button
                        className={classes.filterButton}
                        variant={selectedOrderId === 'all' ? 'contained' : 'outlined'}
                        onClick={() => handleOrderFilterChange('all')}
                      >
                        All
                      </Button>
                    </ButtonGroup>
                  );
                }

                // Extract unique order IDs
                const sessionsFromData = data.sessions;
                const orderIds = [
                  ...new Set(sessionsFromData.filter(session => session.order_id).map(session => session.order_id)),
                ].sort((a, b) => a - b);

                return (
                  <ButtonGroup size='small' color='primary'>
                    <Button
                      className={classes.filterButton}
                      variant={selectedOrderId === 'all' ? 'contained' : 'outlined'}
                      onClick={() => handleOrderFilterChange('all')}
                    >
                      All
                    </Button>
                    {orderIds.map(orderId => (
                      <Button
                        key={`order-filter-${orderId}`}
                        className={classes.filterButton}
                        variant={selectedOrderId === orderId ? 'contained' : 'outlined'}
                        onClick={() => handleOrderFilterChange(orderId)}
                      >
                        #{orderId}
                      </Button>
                    ))}
                  </ButtonGroup>
                );
              }}
            </Subscription>
          </div>

          {/* Date Range Filter */}
          <FormControl variant='outlined' size='small' className={classes.dateRangeFilter}>
            <InputLabel id='date-range-filter-label'>Date Range</InputLabel>
            <Select
              labelId='date-range-filter-label'
              id='date-range-filter'
              value={selectedDateRange}
              onChange={handleDateRangeChange}
              label='Date Range'
            >
              {Object.entries(DATE_RANGES).map(([key, value]) => (
                <MenuItem key={key} value={key}>
                  {value.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* View Mode Toggle */}
          <div className={classes.viewToggleContainer}>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={handleViewModeChange}
              aria-label='View mode'
              size='small'
            >
              <ToggleButton value='sessions' aria-label='Sessions view' className={classes.toggleButton}>
                <ViewModuleIcon className={classes.toggleIcon} />
                <Typography variant='caption' className={classes.toggleLabel}>
                  Sessions
                </Typography>
              </ToggleButton>
              <ToggleButton value='photos' aria-label='Photos view' className={classes.toggleButton}>
                <ViewComfyIcon className={classes.toggleIcon} />
                <Typography variant='caption' className={classes.toggleLabel}>
                  Photos
                </Typography>
              </ToggleButton>
            </ToggleButtonGroup>
          </div>
        </div>

        <Subscription
          subscription={SESSIONS_SUBSCRIPTION_MINIMAL}
          variables={{
            startDate: dateRange.start,
            endDate: dateRange.end,
          }}
        >
          {result => {
            const { loading, error, data } = result || {};
            if (loading) {
              return (
                <div className={classes.centered}>
                  <CircularProgress />
                  <Typography variant='body1' style={{ marginTop: '16px' }}>
                    Loading sessions...
                  </Typography>
                </div>
              );
            }

            if (error) {
              return (
                <div className={classes.centered}>
                  <Typography variant='h6' color='error'>
                    Error loading sessions
                  </Typography>
                  <Typography variant='body2' color='error'>
                    {error.message}
                  </Typography>
                </div>
              );
            }

            if (!data || !data.sessions || data.sessions.length === 0) {
              return (
                <Grid item xs={12}>
                  <div className={classes.centered} style={{ height: 'auto', marginTop: '50px' }}>
                    <Typography variant='body1'>
                      No {viewMode === 'sessions' ? 'sessions' : 'photos'} found for the selected date range.
                    </Typography>
                  </div>
                </Grid>
              );
            }

            // Process sessions data
            const sessionsFromData = data.sessions;
            const currentIds = new Set(sessionsFromData.map(s => s.id));

            const processedSessions = sessionsFromData.map(session => {
              const photos = session.photos || [];

              // Helper function to ensure photo has full_url (use thumb_url as fallback)
              const ensurePhotoUrl = photo => {
                if (!photo) return null;
                return {
                  ...photo,
                  full_url: photo.full_url || photo.thumb_url || '',
                };
              };

              const messagePhoto = ensurePhotoUrl(photos.find(photo => photo.name === 'sign.png'));
              const stripPhoto = ensurePhotoUrl(photos.find(photo => photo.name === 'strip.jpg'));
              const photosForCard = photos
                .filter(photo => photo.name !== 'sign.png' && photo.name !== 'strip.jpg')
                .map(ensurePhotoUrl);

              return {
                ...session,
                derivedMessage: messagePhoto,
                derivedStrip: stripPhoto,
                derivedPhotos: photosForCard,
                isNew: !prevSessionIds.current.has(session.id),
              };
            });

            // Filter sessions by selected order ID
            const filteredSessions =
              selectedOrderId === 'all'
                ? processedSessions
                : processedSessions.filter(session => session.order_id === selectedOrderId);

            // Update previous session IDs for next render
            prevSessionIds.current = currentIds;

            // Create list of all individual photos for photo view with session data
            const allFilteredPhotos =
              viewMode === 'photos'
                ? (selectedOrderId === 'all'
                    ? processedSessions
                    : processedSessions.filter(session => session.order_id === selectedOrderId)
                  ).flatMap(session => {
                    // Make sure we're passing all required session data
                    return (session.derivedPhotos || []).map(photo => {
                      // Extract event_name and booth_id from session.order
                      const eventName = session.order?.event_name || null;
                      const boothId = session.order?.booth_id || null;

                      return {
                        ...photo,
                        sessionIsNew: session.isNew,
                        sessionId: session.id,
                        orderId: session.order_id,
                        createdat: session.createdat || photo.createdat,
                        boothId: boothId,
                        eventName: eventName,
                      };
                    });
                  })
                : [];

            // No results after filtering
            if (
              (viewMode === 'sessions' && filteredSessions.length === 0) ||
              (viewMode === 'photos' && allFilteredPhotos.length === 0)
            ) {
              return (
                <Grid item xs={12}>
                  <div className={classes.centered} style={{ height: 'auto', marginTop: '50px' }}>
                    <Typography variant='body1'>
                      No {viewMode === 'sessions' ? 'sessions' : 'photos'} found for the selected filter.
                    </Typography>
                  </div>
                </Grid>
              );
            }

            // Calculate dimensions for virtualization
            const containerWidth = typeof window !== 'undefined' ? window.innerWidth - 48 : 1200;
            const photoItemSize = Math.floor((containerWidth - 16 * (columnCount + 1)) / columnCount);
            const photoRowCount = Math.ceil(allFilteredPhotos.length / columnCount);

            // Calculate sessions per row - 6 on desktop, responsive for smaller screens
            let sessionsPerRow = 6; // Desktop default
            if (typeof window !== 'undefined') {
              const width = window.innerWidth;
              if (width < 960) sessionsPerRow = 3; // Tablet
              else if (width < 1280) sessionsPerRow = 4; // Small desktop
              else if (width < 1920) sessionsPerRow = 6; // Desktop
              else sessionsPerRow = 6; // Large desktop
            }

            const sessionRowCount = Math.ceil(filteredSessions.length / sessionsPerRow);
            const sessionRowHeight = 350; // Approximate height per session row
            // Use viewport height for virtualization (faster rendering)
            // But style to allow page-level scrolling without embedded scrollbars
            const viewportHeight = typeof window !== 'undefined' ? window.innerHeight - 200 : 800;
            // Calculate total content height for reference (but use viewport for virtualization)
            const totalSessionHeight = sessionRowCount * sessionRowHeight;
            const totalPhotoHeight = photoRowCount * (photoItemSize + 16);

            return (
              <>
                {viewMode === 'sessions' ? (
                  <div className={classes.virtualListContainer} ref={containerRef}>
                    <VirtualList
                      height={viewportHeight}
                      itemCount={sessionRowCount}
                      itemSize={sessionRowHeight}
                      width='100%'
                      itemData={{
                        sessions: filteredSessions,
                        classes,
                        containerWidth,
                        sessionsPerRow,
                      }}
                    >
                      {SessionRow}
                    </VirtualList>
                  </div>
                ) : (
                  <div className={classes.virtualGridContainer} ref={containerRef}>
                    <VirtualGrid
                      columnCount={columnCount}
                      columnWidth={photoItemSize + 16}
                      height={viewportHeight}
                      rowCount={photoRowCount}
                      rowHeight={photoItemSize + 16}
                      width={containerWidth}
                      itemData={{
                        photos: allFilteredPhotos,
                        columnCount,
                        onPhotoClick: openPhotoLightbox,
                        classes,
                        itemSize: photoItemSize,
                      }}
                    >
                      {PhotoCell}
                    </VirtualGrid>
                  </div>
                )}
              </>
            );
          }}
        </Subscription>
      </div>
    </div>
  );
}

export default RecentSessionsPage;
